import { IHsToken } from "../store/installation.store"
import logo_ms_sql from "../assets/images/MS_SQL.png"
import logo_sap_hana from "../assets/images/sap_hana_logo.jpeg"
import logo_odata from "../assets/images/odata.png"
import logo_netsuite from "../assets/images/netsuite.png"
import logo_erp from "../assets/images/operations/db_icon.png"

export enum InstallationStatus {
    COMPLETED_SUCCESSFULLY = "COMPLETED_SUCCESSFULLY",
    READY = "READY",
    RUNNING = "RUNNING",
    PENDING = "PENDING",
    ERROR = "ERROR",
    IDLE = "IDLE"
}

type DatabaseColumn = {
    name: string;
    dataType: string;
}

export type DatabaseTable = {
    name: string;
    columns: DatabaseColumn[];
}

export type InstallationTier = {
    maxObjectProperties: number;
    minFrequency: number;
    name: string;
    objectsWhitelist: string[];
    expirationDate?: string | null;
    licenses?: InstallationTierLicense[] | null;
}
export type InstallationTierLicense = {
    id: string;
    expirationDate?: string | null;
}
export enum EEntitlementBannerSeverity {
    WARNING = "warning",
    CRITICAL = "critical"
}
export type InstallationExpirationBanner = {
    severity: EEntitlementBannerSeverity;
    title: string;
    description: string;
    renewalDate?: string;
    dismissible: boolean;
    key: string;
}
export enum ERealTimeStatus {
    NOT_PURCHASED = "notPurchased",
    NEEDS_CONFIGURATION = "needsConfiguration",
    CONFIGURED = "configured"
}
export enum EInstallationTier {
    TRIAL = "TRIAL",
    ONE_WAY = "ONE_WAY",
    TWO_WAY = "TWO_WAY"
}
export enum EInstallationAddonLicense {
    ERP_VIEW = "ERP_VIEW",
    REAL_TIME = "REAL_TIME"
}
type Installation = {
    id: string;
    tenantId: string;
    enabled: boolean;
    frequency: number;
    name: string;
    createdAt: string;
    lastRunAt: string;
    state: InstallationStatus;
    hsTokens: IHsToken;
    databaseTables: DatabaseTable[];
    tier: InstallationTier;
    dbmsTech: IDbmsTechEnum;
    realTime?: {
        clientSecret?: string;
    };
}

const normalizeLicenseId = (value?: string | null): string => (value || "").replace(/-/g, "_").toUpperCase();
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const getExpirationDate = (expirationDate?: string | null): Date | null => {
    if (!expirationDate) {
        return null;
    }

    const expiresAt = new Date(expirationDate);

    if (Number.isNaN(expiresAt.getTime())) {
        return null;
    }

    return expiresAt;
}

const getDaysUntilExpiration = (expirationDate?: string | null, referenceDate: Date = new Date()): number | null => {
    const expiresAt = getExpirationDate(expirationDate);

    if (!expiresAt) {
        return null;
    }

    return Math.ceil((expiresAt.getTime() - referenceDate.getTime()) / MS_PER_DAY);
}

const formatRenewalDate = (expirationDate?: string | null): string | undefined => {
    const expiresAt = getExpirationDate(expirationDate);

    if (!expiresAt) {
        return undefined;
    }

    return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
    }).format(expiresAt);
}

export const isEntitlementActive = (expirationDate?: string | null): boolean => {
    if (!expirationDate) {
        return true;
    }

    const expiresAt = new Date(expirationDate);

    if (Number.isNaN(expiresAt.getTime())) {
        return true;
    }

    return expiresAt.getTime() > Date.now();
}

export const isCoreLicenseExpired = (
    tier: InstallationTier | undefined,
    referenceDate: Date = new Date()
): boolean => {
    const coreDaysUntilExpiration = getDaysUntilExpiration(tier?.expirationDate, referenceDate);
    return coreDaysUntilExpiration !== null && coreDaysUntilExpiration <= 0;
}

export const isTwoWayTier = (i?: InstallationTier): boolean =>
    normalizeLicenseId(i?.name) === EInstallationTier.TWO_WAY;

export const isTrialTier = (i?: InstallationTier): boolean =>
    normalizeLicenseId(i?.name) === EInstallationTier.TRIAL;

export const getInstallationTierLabel = (tier?: InstallationTier): string => {
    switch (normalizeLicenseId(tier?.name)) {
        case EInstallationTier.TRIAL:
            return "Trial";
        case EInstallationTier.ONE_WAY:
            return "One-Way";
        case EInstallationTier.TWO_WAY:
            return "Two-Way";
        default:
            return tier?.name || "-";
    }
}

export const getInstallationAddonLicenseLabel = (licenseId?: string | null): string => {
    switch (normalizeLicenseId(licenseId)) {
        case EInstallationAddonLicense.ERP_VIEW:
            return "ERP View";
        case EInstallationAddonLicense.REAL_TIME:
            return "Real time sync";
        default:
            return licenseId || "-";
    }
}

export const getInstallationExpirationBanner = (
    tier: InstallationTier | undefined,
    referenceDate: Date = new Date()
): InstallationExpirationBanner | undefined => {
    const coreDaysUntilExpiration = getDaysUntilExpiration(tier?.expirationDate, referenceDate);
    const coreRenewalDate = formatRenewalDate(tier?.expirationDate);

    if (coreDaysUntilExpiration !== null && coreDaysUntilExpiration <= 0) {
        return {
            severity: EEntitlementBannerSeverity.CRITICAL,
            title: "Core license expired",
            description: "This installation is now read-only and all syncs are paused",
            dismissible: false,
            key: `core-expired-${tier?.expirationDate || ""}`
        };
    }

    const addonLicenses = tier?.licenses || [];
    const expiredAddonLicenses = addonLicenses.filter((license) => {
        const daysUntilExpiration = getDaysUntilExpiration(license.expirationDate, referenceDate);
        return daysUntilExpiration !== null && daysUntilExpiration <= 0;
    });
    const expiringAddonLicenses = addonLicenses.filter((license) => {
        const daysUntilExpiration = getDaysUntilExpiration(license.expirationDate, referenceDate);
        return daysUntilExpiration !== null && daysUntilExpiration > 0 && daysUntilExpiration <= 14;
    });

    if (coreDaysUntilExpiration !== null && coreDaysUntilExpiration > 0 && coreDaysUntilExpiration <= 30) {
        const addonMessage = expiredAddonLicenses.length
            ? ` ${expiredAddonLicenses.length === 1 ? "1 add-on has expired." : `${expiredAddonLicenses.length} add-ons have expired.`}`
            : expiringAddonLicenses.length
                ? ` ${expiringAddonLicenses.length === 1 ? "1 add-on is expiring soon." : `${expiringAddonLicenses.length} add-ons are expiring soon.`}`
                : "";

        return {
            severity: coreDaysUntilExpiration <= 1 ? EEntitlementBannerSeverity.CRITICAL : EEntitlementBannerSeverity.WARNING,
            title: `Core license expires in ${coreDaysUntilExpiration} ${coreDaysUntilExpiration === 1 ? "day" : "days"}`,
            description: `Renew before ${coreRenewalDate || "the expiration date"} to keep scheduled and manual syncs active.${addonMessage}`,
            renewalDate: coreRenewalDate,
            dismissible: true,
            key: `core-expiring-${coreDaysUntilExpiration}-${tier?.expirationDate || ""}-${expiredAddonLicenses.length}-${expiringAddonLicenses.length}`
        };
    }

    if (expiredAddonLicenses.length) {
        const addon = expiredAddonLicenses[0];
        const addonLabel = expiredAddonLicenses.length === 1
            ? `${getInstallationAddonLicenseLabel(addon.id)} license expired`
            : `${expiredAddonLicenses.length} add-on licenses expired`;
        const addonDescription = expiredAddonLicenses.length === 1
            ? `${getInstallationAddonLicenseLabel(addon.id)} is unavailable until it is renewed`
            : "These add-ons are unavailable until they are renewed";

        return {
            severity: EEntitlementBannerSeverity.CRITICAL,
            title: addonLabel,
            description: addonDescription,
            dismissible: true,
            key: `addons-expired-${expiredAddonLicenses.map((license) => `${license.id}-${license.expirationDate || ""}`).join("-")}`
        };
    }

    if (expiringAddonLicenses.length) {
        const sortedExpiringAddons = [...expiringAddonLicenses].sort((a, b) => {
            const aDays = getDaysUntilExpiration(a.expirationDate, referenceDate) || 0;
            const bDays = getDaysUntilExpiration(b.expirationDate, referenceDate) || 0;
            return aDays - bDays;
        });
        const addon = sortedExpiringAddons[0];
        const addonDaysUntilExpiration = getDaysUntilExpiration(addon.expirationDate, referenceDate) || 0;
        const addonRenewalDate = formatRenewalDate(addon.expirationDate);

        return {
            severity: addonDaysUntilExpiration <= 1 ? EEntitlementBannerSeverity.CRITICAL : EEntitlementBannerSeverity.WARNING,
            title: expiringAddonLicenses.length === 1
                ? `${getInstallationAddonLicenseLabel(addon.id)} license expires in ${addonDaysUntilExpiration} ${addonDaysUntilExpiration === 1 ? "day" : "days"}`
                : `${expiringAddonLicenses.length} add-on licenses are expiring`,
            description: expiringAddonLicenses.length === 1
                ? `Renew before ${addonRenewalDate || "the expiration date"}`
                : "Renew these add-ons to keep their features available",
            renewalDate: addonRenewalDate,
            dismissible: true,
            key: `addons-expiring-${expiringAddonLicenses.map((license) => `${license.id}-${license.expirationDate || ""}`).join("-")}`
        };
    }

    return undefined;
}

export const hasInstallationAddonLicense = (
    tier: InstallationTier | undefined,
    addonId: EInstallationAddonLicense
): boolean => {
    if (!tier || !isEntitlementActive(tier.expirationDate)) {
        return false;
    }

    return !!tier.licenses?.some(
        (license) =>
            normalizeLicenseId(license.id) === addonId && isEntitlementActive(license.expirationDate)
    );
}

export const getRealTimeStatus = (tier: InstallationTier | undefined, clientSecret?: string | null): ERealTimeStatus => {
    if (!hasInstallationAddonLicense(tier, EInstallationAddonLicense.REAL_TIME)) {
        return ERealTimeStatus.NOT_PURCHASED;
    }

    if (typeof clientSecret === "string" && clientSecret.trim().length > 0) {
        return ERealTimeStatus.CONFIGURED;
    }

    return ERealTimeStatus.NEEDS_CONFIGURATION;
}

export const isObjectInWhitelist = (objectsWhitelist: string[], value: string): boolean => {
    return objectsWhitelist.includes(value) || (value.startsWith("2-") && objectsWhitelist.includes("custom:*"))
}

export enum IDbmsTechEnum {
    MS_SQL = "MS_SQL",
    SAP_HANA = "SAP_HANA",
    ODATA = "ODATA",
    NETSUITE_REST = "NETSUITE_REST"
}

// ponytail: these adapters emit schema.table; add parsing only if that API contract changes.
export const getDatabaseTableDisplayName = (
    tableName: string,
    dbmsTech?: IDbmsTechEnum
): string => (dbmsTech === IDbmsTechEnum.MS_SQL || dbmsTech === IDbmsTechEnum.SAP_HANA)
    ? tableName.slice(tableName.lastIndexOf(".") + 1)
    : tableName;

export const resolveDatabaseTableName = (
    databaseTables: DatabaseTable[],
    tableName?: string,
    dbmsTech?: IDbmsTechEnum
): string => {
    const exactMatch = databaseTables.find((table) => table.name === tableName);
    const legacyMatches = databaseTables.filter(
        (table) => getDatabaseTableDisplayName(table.name, dbmsTech) === tableName
    );

    return exactMatch?.name || (legacyMatches.length === 1 ? legacyMatches[0].name : tableName || "");
}

export const getIconForDB = (s?: IDbmsTechEnum): string => {
    switch (s) {
        case IDbmsTechEnum.MS_SQL:
            return logo_ms_sql
        case IDbmsTechEnum.SAP_HANA:
            return logo_sap_hana
        case IDbmsTechEnum.ODATA:
            return logo_odata
        case IDbmsTechEnum.NETSUITE_REST:
            return logo_netsuite
        default:
            return logo_erp
    }
}
export const getLabelForDB = (s: IDbmsTechEnum | undefined): string | null => {
    switch (s) {
        case IDbmsTechEnum.MS_SQL:
            return "SQL Server"
        case IDbmsTechEnum.SAP_HANA:
            return "SAP Hana"
        case IDbmsTechEnum.ODATA:
            return "OData"
        case IDbmsTechEnum.NETSUITE_REST:
            return "NetSuite"
        default:
            return null
    }
}

export const getLabelForStatus = (s: InstallationStatus): string => {
    switch (s) {
        case InstallationStatus.COMPLETED_SUCCESSFULLY:
            return "views.installations.status.completed"
        case InstallationStatus.RUNNING:
            return "views.installations.status.running"
        case InstallationStatus.PENDING:
            return "views.installations.status.pending"
        case InstallationStatus.READY:
            return "views.installations.status.ready"
        case InstallationStatus.ERROR:
            return "views.installations.status.error"
        case InstallationStatus.IDLE:
            return "views.installations.status.idle"
        default:
            return ""
    }
}
export const getStateForStatus = (s: InstallationStatus): "success" | "error" | "waiting" | "warning" | undefined => {
    switch (s) {
        case InstallationStatus.COMPLETED_SUCCESSFULLY:
            return "success"
        case InstallationStatus.READY:
            return "success"
        case InstallationStatus.RUNNING:
            return "warning"
        case InstallationStatus.PENDING:
            return "waiting"
        case InstallationStatus.ERROR:
            return "error"
        case InstallationStatus.IDLE:
            return "waiting"
        default:
            return undefined
    }
}

export default Installation;
